// Legal content for Build My Trade.
// Confirmed by the owner (July 2026): sole trader trading as "Build My Trade",
// single contact inbox info@buildmytrade.uk, 50% deposit / balance on completion,
// jurisdiction England & Wales. Owner chose not to publish a full postal address.

export const LEGAL_PAGES = {
  privacy: {
    title: 'Privacy Policy',
    intro:
      'This policy explains what personal information Build My Trade collects through this website, why, and what you can do about it. It’s written to be read, not to hide things in.',
    sections: [
      {
        h: 'Who we are',
        paragraphs: [
          'Build My Trade is a UK sole trader business providing digital services to tradespeople. For anything to do with your personal data, contact us at info@buildmytrade.uk. If you need our service address for any legal purpose, just ask at the same address and we’ll provide it.',
          'Build My Trade is the “data controller” for the personal information collected through this website, which means we’re responsible for looking after it under UK data protection law (UK GDPR and the Data Protection Act 2018).',
        ],
      },
      {
        h: 'What we collect',
        bullets: [
          'Enquiry form details: your name, business name, email address, phone number (if you give it), type of trade, current online presence, package of interest, preferred contact method and the project details you write to us.',
          'Marketing preference: whether you opted in to hear from us occasionally.',
          'Basic technical data: standard server logs (such as your browser type and IP address) used to keep the website secure and running.',
        ],
      },
      {
        h: 'How we use it',
        bullets: [
          'To respond to your enquiry and provide quotes and services you ask for.',
          'To deliver work you’ve commissioned and manage our working relationship.',
          'To send occasional marketing updates — only if you’ve explicitly opted in, and you can withdraw at any time.',
          'To keep the website secure and fix problems.',
        ],
      },
      {
        h: 'Our lawful bases',
        paragraphs: [
          'We process enquiry details to take steps at your request before entering a contract, and under our legitimate interest in running and improving a small business. Marketing emails rely on your consent, which you can withdraw at any time by contacting info@buildmytrade.uk.',
        ],
      },
      {
        h: 'Who we share it with',
        paragraphs: [
          'We never sell your information. Enquiry notifications are delivered through our email processing provider and enquiry records are stored in a secured database. Service providers only handle your data to provide their service to us and under appropriate safeguards.',
        ],
      },
      {
        h: 'How long we keep it',
        paragraphs: [
          'Enquiry records are kept for up to 24 months unless you become a customer (in which case we keep what we need for the contract and our legal obligations) or you ask us to delete them sooner.',
        ],
      },
      {
        h: 'Your rights',
        paragraphs: [
          'You can ask to see, correct or delete your personal information, object to or restrict how we use it, and ask for a copy in a portable format. Contact info@buildmytrade.uk and we’ll respond within one month.',
          'If you’re unhappy with how we handle your data, you can complain to the Information Commissioner’s Office (ICO) at ico.org.uk. We’d appreciate the chance to put things right first.',
        ],
      },
      {
        h: 'Cookies',
        paragraphs: ['See our Cookie Policy for what this site stores in your browser — in short, very little.'],
      },
      {
        h: 'Changes to this policy',
        paragraphs: [
          'If we change how this website handles personal information, we’ll update this page before or when the change happens.',
        ],
      },
    ],
  },

  cookies: {
    title: 'Cookie Policy',
    intro:
      'This site is intentionally light on cookies. This policy reflects what the website actually does today — not a generic template.',
    sections: [
      {
        h: 'What this site stores',
        paragraphs: [
          'The website itself does not set tracking cookies. Your cookie preference (if analytics is ever enabled) is stored in your browser’s local storage so we remember your choice. That’s it.',
        ],
      },
      {
        h: 'Analytics (off by default)',
        paragraphs: [
          'This website does not currently run any analytics or tracking services. The site is prepared for Google Analytics 4, but it will only ever be switched on with a consent banner first — if you decline, no analytics cookies are set. If we enable analytics in future, we’ll update this policy to list the cookies involved before doing so.',
        ],
      },
      {
        h: 'Managing cookies',
        paragraphs: [
          'You can clear or block cookies and site data at any time through your browser settings. Blocking the storage this site uses won’t stop it working — the only effect is that we may ask about cookies again on your next visit.',
        ],
      },
      {
        h: 'Questions',
        paragraphs: ['Contact info@buildmytrade.uk with any questions about cookies or privacy.'],
      },
    ],
  },

  terms: {
    title: 'Website Terms & Conditions',
    intro:
      'These terms cover your use of this website. The terms that govern work we do for you are in the Website Design & Service Terms.',
    sections: [
      {
        h: 'Who we are',
        paragraphs: [
          'This website is operated by Build My Trade, a UK sole trader business providing digital services to tradespeople. Contact: info@buildmytrade.uk.',
        ],
      },
      {
        h: 'Using this website',
        paragraphs: [
          'The content on this site is for general information about our services. It isn’t professional advice, and package details shown here are an invitation to request a quote rather than a binding offer — every project is confirmed in writing before work begins.',
        ],
      },
      {
        h: 'Intellectual property',
        paragraphs: [
          'The design, text and branding of this website belong to Build My Trade. You’re welcome to browse and share links, but please don’t copy the site’s content or design for your own commercial use.',
        ],
      },
      {
        h: 'Accuracy and availability',
        paragraphs: [
          'We work to keep the site accurate and available, but we don’t warrant that it will be uninterrupted or error-free. Prices and package contents may be updated; the package confirmed in your written quote is the one that applies to your project.',
        ],
      },
      {
        h: 'Links to other sites',
        paragraphs: [
          'If this site links to third-party websites (for example platform providers), we’re not responsible for their content or policies.',
        ],
      },
      {
        h: 'Liability',
        paragraphs: [
          'Nothing in these terms excludes or limits liability that cannot be excluded by law. Otherwise, to the extent permitted by law, we’re not liable for losses arising from use of, or inability to use, this website.',
        ],
      },
      {
        h: 'Governing law',
        paragraphs: ['These terms are governed by the laws of England and Wales.'],
      },
    ],
  },

  'service-terms': {
    title: 'Website Design & Service Terms',
    intro:
      'These terms apply when you buy a package or monthly plan from Build My Trade. They’re written in plain English because that’s how we do everything. Your written quote, together with these terms, forms our agreement.',
    sections: [
      {
        h: 'Scope of work and deliverables',
        paragraphs: [
          'Each package includes exactly the deliverables listed on the pricing page at the time you book, as confirmed in your written quote. Packages build on each other as described — you’re never charged twice for something already included.',
          'Anything not listed — extra pages, copywriting or photography beyond the agreed scope, paid advertising, premium plugins or third-party subscriptions — is quoted separately before work starts. There are no surprise charges.',
        ],
      },
      {
        h: 'Payment and deposits',
        paragraphs: [
          'Prices are in GBP and cover the stated deliverables. A 50% deposit is payable before work begins, with the balance due on completion and before final handover.',
          'Domain registration, hosting and website platform subscriptions are paid by you directly to the provider, in your name, unless we agree otherwise in writing. We’ll always tell you the expected ongoing costs before you commit.',
        ],
      },
      {
        h: 'What we need from you',
        bullets: [
          'Accurate business information, service lists and the areas you cover.',
          'Photographs of your work and any existing branding.',
          'Timely feedback and approvals at agreed stages.',
          'Access to accounts we’re setting up or improving (for example Google, Facebook or Instagram), or a willingness to follow guided steps.',
        ],
        paragraphs: [
          'If information or approvals are delayed, project dates move accordingly — we’ll always tell you what we’re waiting on.',
        ],
      },
      {
        h: 'Revisions',
        paragraphs: [
          'Packages include the stated number of reasonable revision rounds (for example, two rounds in Look Professional). A revision round is a consolidated set of feedback on the work presented. Additional rounds or changes of direction after approval are quoted separately.',
        ],
      },
      {
        h: 'Timelines and dependencies',
        paragraphs: [
          'We give realistic estimates, not invented guarantees. Timelines depend on receiving your information and approvals, and on third parties we don’t control — including Google Business Profile verification, domain registration and platform approvals. We’ll keep you informed throughout.',
        ],
      },
      {
        h: 'Ownership and handover',
        paragraphs: [
          'On full payment, the website design, content we’ve created for you and branding deliverables are yours. Your domain, website and business profiles are held in your name wherever the platform allows. Third-party software, themes and platforms remain subject to their own licences and terms.',
        ],
      },
      {
        h: 'Third-party platforms',
        paragraphs: [
          'Websites, profiles and listings run on third-party platforms (for example Google, Meta, hosting and website platforms). Their availability, rules and pricing are outside our control. If a platform change affects your service, we’ll explain your options honestly.',
        ],
      },
      {
        h: 'Cancellation and refunds',
        paragraphs: [
          'See our Cancellation & Refund Policy. In summary: if you’re a consumer buying at a distance, you normally have a 14-day cooling-off period; if you ask us to start work sooner, you pay a proportionate amount for work done if you then cancel. Nothing in these terms removes your statutory rights.',
        ],
      },
      {
        h: 'Monthly support',
        paragraphs: [
          'Monthly plans are rolling and optional. You can cancel before your next billing date and nothing further is taken. Each plan includes the deliverables listed on the pricing page; anything beyond that scope is quoted separately. We may end a monthly plan with 30 days’ notice, for example if the service is withdrawn — any prepaid unused period would be refunded.',
        ],
      },
      {
        h: 'What we don’t guarantee',
        paragraphs: [
          'We don’t guarantee search engine rankings, a position in Google’s local results, Google verification within a particular timeframe, enquiry volumes, sales or social media reach. No honest provider can. We guarantee the deliverables: the work listed, done properly.',
        ],
      },
      {
        h: 'Your statutory rights',
        paragraphs: [
          'If you’re a consumer, the Consumer Rights Act 2015 gives you rights including that services are provided with reasonable care and skill. Nothing in these terms affects those rights. If something we’ve done falls short, tell us and we’ll put it right.',
        ],
      },
    ],
  },

  refunds: {
    title: 'Cancellation & Refund Policy',
    intro:
      'Fair, straightforward cancellation terms. If you’re unsure where you stand, just ask — we’d rather sort something out properly than hide behind small print.',
    sections: [
      {
        h: 'One-off packages',
        paragraphs: [
          'If you’re a consumer buying at a distance (online or by phone), you normally have 14 days from booking to change your mind and receive a full refund.',
          'If you ask us to start work within those 14 days and then cancel, you pay a reasonable, proportionate amount for the work already done, and we refund the rest. Once the work is completed within the cooling-off period at your request, the right to cancel is lost for that completed work.',
        ],
      },
      {
        h: 'Monthly plans',
        paragraphs: [
          'Monthly plans are rolling. Cancel before your next billing date and nothing further is taken. There are no partial refunds for a month already started — the service simply continues to the end of the paid period.',
        ],
      },
      {
        h: 'Third-party costs',
        paragraphs: [
          'Domain registrations, hosting and platform subscriptions are bought in your name from third parties and are generally non-refundable by them. Where we’ve paid a third party on your behalf with your agreement, we’ll pass on any refund we’re able to obtain.',
        ],
      },
      {
        h: 'How to request a cancellation or refund',
        paragraphs: [
          'Contact info@buildmytrade.uk with your name, business name and what you’d like to cancel. Approved refunds are paid to your original payment method within 14 days.',
        ],
      },
      {
        h: 'Your statutory rights',
        paragraphs: [
          'This policy sits alongside — never instead of — your statutory rights, including those under the Consumer Contracts Regulations 2013 and the Consumer Rights Act 2015.',
        ],
      },
    ],
  },

  accessibility: {
    title: 'Accessibility Statement',
    intro:
      'Build My Trade wants this website to be usable by everyone, including people using assistive technology. Our customers come from every walk of life — the site should too.',
    sections: [
      {
        h: 'Our commitment',
        paragraphs: [
          'We aim for this website to meet WCAG 2.2 Level AA. That target shapes how the site is designed and built: clear heading structure, keyboard-accessible navigation and forms, visible focus indicators, sufficient colour contrast, alt text for meaningful images, and respect for reduced-motion settings.',
        ],
      },
      {
        h: 'Known limitations',
        paragraphs: [
          'We’re not aware of any part of this website that deliberately falls short of that target, and accessibility is checked as part of every site update. If you do find something, telling us is the fastest way to get it fixed.',
        ],
      },
      {
        h: 'Feedback and problems',
        paragraphs: [
          'If you find any part of this site hard to use, contact info@buildmytrade.uk and tell us what happened and which page you were on. We’ll acknowledge your report and aim to fix genuine issues promptly.',
        ],
      },
      {
        h: 'Enforcement',
        paragraphs: [
          'If you’re not happy with how we respond to an accessibility concern, you can contact the Equality Advisory and Support Service (EASS), which advises on the Equality Act 2010.',
        ],
      },
    ],
  },
};

export const LEGAL_NAV = [
  { slug: 'privacy', label: 'Privacy Policy' },
  { slug: 'cookies', label: 'Cookie Policy' },
  { slug: 'terms', label: 'Website Terms' },
  { slug: 'service-terms', label: 'Service Terms' },
  { slug: 'refunds', label: 'Cancellations & Refunds' },
  { slug: 'accessibility', label: 'Accessibility' },
];
